import { useNavigate, useSearch } from "@tanstack/react-router";
import type { BlocklistImportPreviewRow } from "@unbox/api";
import {
  AlertCircle,
  LinkBroken02,
  ReverseLeft,
  Ruler,
  SearchMd,
  SlashCircle01,
  Upload01,
  UsersX,
  UserX01,
  XClose,
} from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Selection } from "react-aria-components";
import {
  useBlockedCreatorRowsFetcher,
  useBlockedCreatorsCount,
  useBlockedCreatorsList,
  useBlocklistImportPreview,
  useBlocklistRulesCount,
  useDownloadBlocklistImportTemplate,
} from "@/api/blocklist";
import { useBlocklistRules } from "@/api/blocklist-rules";
import { APIError } from "@/api/error";
import { usePagination } from "@/api/pagination";
import { useDismissHero } from "@/api/user-preferences";
import { PageHero, PageHeroKey } from "@/components/application/page-hero/page-hero";
import { SelectAllPagesButton } from "@/components/application/table/select-all-pages-button";
import { Avatar } from "@/components/base/avatar/avatar";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Input } from "@/components/base/input/input";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { BlocklistImportErrorDrawer } from "@/components/blocklist/blocklist-import-error-drawer";
import { BlocklistImportReviewDrawer } from "@/components/blocklist/blocklist-import-review-drawer";
import { BulkUnblockCreatorsModal } from "@/components/blocklist/bulk-unblock-creators-modal";
import { BlocklistRulesPanel } from "@/components/blocklist/rules/blocklist-rules-panel";
import { RuleBuilderDrawer } from "@/components/blocklist/rules/rule-builder-drawer";
import { UnblockCreatorModal } from "@/components/blocklist/unblock-creator-modal";
import { LoadingPageState } from "@/components/loading-page-state";
import { PageLayout } from "@/components/page-layout";
import { useCache } from "@/hooks/use-cache";
import { useCrossPageSelection } from "@/hooks/use-cross-page-selection";
import { useDebounce } from "@/hooks/use-debounce";
import { useSelectAllAcrossPages } from "@/hooks/use-select-all-across-pages";
import { CohortLibraryModal } from "@/pages/creator-cohorts/cohort-library-modal";
import { CreatorDetails } from "@/pages/creators/creator-details";
import { useMerchant, useWorkspaceId } from "@/providers/MerchantProvider";
import type { blockedCreatorsRoute } from "@/router";
import { getCreatorName } from "@/utils/creator-name";
import { BlockedCreatorsEmptyState } from "./blocked-creators-empty-state";
import { BlockedCreatorsTable } from "./blocked-creators-table";

const NUDGE_DISMISSED_KEY = (merchantId: string) => `blocked-creators-nudge-dismissed-${merchantId}`;

type BlockedCreatorData = {
  creatorOpenId: string;
};

type UnblockTarget = { creatorOpenId: string; creatorName: string };

export function BlockedCreatorsPage() {
  const merchant = useMerchant();
  const workspaceId = useWorkspaceId();
  const navigate = useNavigate();
  const { creator_id } = useSearch({ strict: false }) as ReturnType<(typeof blockedCreatorsRoute)["useSearch"]>;
  const [selectedCreatorId, setSelectedCreatorId] = useState<string | undefined>(creator_id);
  const dismissHero = useDismissHero();
  const pagination = usePagination({ offset: 0, limit: 20 });
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const [nudgeDismissed, setNudgeDismissed] = useState(() => {
    try {
      return localStorage.getItem(NUDGE_DISMISSED_KEY(merchant)) === "true";
    } catch {
      return false;
    }
  });
  const [unblockTarget, setUnblockTarget] = useState<UnblockTarget>();
  const [bulkUnblockOpen, setBulkUnblockOpen] = useState(false);
  const [rulesPanelOpen, setRulesPanelOpen] = useState(false);
  const [quickCreateRuleOpen, setQuickCreateRuleOpen] = useState(false);
  const [cohortLibraryOpen, setCohortLibraryOpen] = useState(false);
  const openCohortLibrary = useCallback(() => setCohortLibraryOpen(true), []);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [wrongFileMessage, setWrongFileMessage] = useState<string | null>(null);
  const [importPreviewRows, setImportPreviewRows] = useState<BlocklistImportPreviewRow[] | null>(null);
  const importPreview = useBlocklistImportPreview();
  const downloadImportTemplate = useDownloadBlocklistImportTemplate();

  const handleImportFileSelected = async (file: File | undefined) => {
    if (!file) return;
    try {
      const csv = await file.text();
      const { rows } = await importPreview.mutateAsync(csv);
      setImportPreviewRows(rows);
    } catch (err) {
      if (err instanceof APIError && err.isKind("ValidationError")) {
        setWrongFileMessage(err.data.message);
      } else {
        throw err;
      }
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    pagination.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const { data, isPending } = useBlockedCreatorsList({
    search: debouncedSearch,
    offset: pagination.pagination.offset,
    limit: pagination.pagination.limit,
  });
  const { data: countData } = useBlockedCreatorsCount({ search: debouncedSearch });
  const { data: rulesCountData } = useBlocklistRulesCount();
  const { data: rulesListData, isLoading: isLoadingRules } = useBlocklistRules();
  const loadedItems = useCache(data?.items);
  const items = data?.items ?? [];
  const total = countData?.total ?? 0;
  const totalPages = Math.ceil(total / pagination.pagination.limit);
  const activeRulesCount = rulesCountData?.total ?? 0;

  // ── Selection ──────────────────────────────────────────────────────────────
  const {
    selectedKeys: selected,
    isAllPagesSelected,
    selectedCount,
    clear: clearSelection,
    selectAllAcrossPages,
    createSelectionHandler,
  } = useCrossPageSelection<(typeof items)[number], BlockedCreatorData>({
    getKey: useCallback((item) => item.creatorOpenId, []),
    toData: useCallback((item) => ({ creatorOpenId: item.creatorOpenId }), []),
  });

  const fetchChunk = useBlockedCreatorRowsFetcher(debouncedSearch);
  const selectAll = useSelectAllAcrossPages({
    fetchChunk,
    total,
    loadedCount: items.length,
    selection: { selectedCount, isAllPagesSelected, selectAllAcrossPages, clear: clearSelection },
    resetKey: debouncedSearch,
  });
  const handleClearSelection = selectAll.clear;

  const handleSelectionChange = useMemo(() => createSelectionHandler(items), [createSelectionHandler, items]);

  // ── Nudge ──────────────────────────────────────────────────────────────────
  const manualBlocks = items.filter((i) => i.origin === "manual");
  const manualBlockCount = manualBlocks.length;
  const nudgeAvatars = manualBlocks.slice(0, 2);
  const nudgeHiddenCount = manualBlockCount - nudgeAvatars.length;
  const showNudge = !nudgeDismissed && manualBlockCount > 0;

  function dismissNudge() {
    setNudgeDismissed(true);
    try {
      localStorage.setItem(NUDGE_DISMISSED_KEY(merchant), "true");
    } catch {
      // ignore
    }
  }

  const isFirstLoad = (isPending && loadedItems === undefined) || isLoadingRules;
  if (!debouncedSearch && isFirstLoad) return <LoadingPageState />;

  const isEmpty = !isPending && items.length === 0 && !debouncedSearch;

  // The full-bleed first run is for a brand-new blocklist only: no blocked
  // creators and no rules at all (enabled or not). A workspace that has set up
  // any rule keeps the normal view so those rules stay reachable.
  const showFirstRun = isEmpty && rulesListData !== undefined && rulesListData.items.length === 0;

  if (showFirstRun) {
    return (
      <>
        <BlockedCreatorsEmptyState
          onBrowseCreators={() =>
            navigate(
              workspaceId
                ? {
                    to: "/workspace/$workspaceId/merchant/$merchantId/videos" as string,
                    params: { workspaceId, merchantId: merchant },
                    search: { fromEmptyState: true },
                  }
                : {
                    to: "/merchant/$merchantId/videos",
                    params: { merchantId: merchant },
                    search: { fromEmptyState: true },
                  },
            )
          }
          onCreateRule={() => setQuickCreateRuleOpen(true)}
        />
        <RuleBuilderDrawer
          open={quickCreateRuleOpen}
          onOpenChange={setQuickCreateRuleOpen}
          onCreated={() => dismissHero.mutate(PageHeroKey.BlockedCreators)}
        />
      </>
    );
  }

  return (
    <PageLayout
      title="Blocked Creators"
      icon={UserX01}
      subtitle="Creators excluded from recommendations, search and imports."
      callToAction={
        <div className="flex flex-row items-center gap-2">
          <Button color="secondary" iconLeading={Upload01} onClick={() => fileInputRef.current?.click()}>
            Import CSV
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => handleImportFileSelected(e.target.files?.[0])}
          />
          <Button color="secondary" iconLeading={Ruler} onClick={() => setRulesPanelOpen(true)}>
            See Rules {activeRulesCount > 0 ? `(${activeRulesCount})` : ""}
          </Button>
        </div>
      }
    >
      <PageHero
        pageKey={PageHeroKey.BlockedCreators}
        title="Keep a creator out of every collaboration."
        text1={{ icon: LinkBroken02, message: "Auto remove collaboration links from creator videos." }}
        text2={{ icon: SlashCircle01, message: "Reject sample requests automatically." }}
        text3={{ icon: UsersX, message: "Hide them from creator recommendations." }}
        image="/heroes/blocked-creators.png"
      />

      {/* Nudge banner */}
      {showNudge && (
        <div className="flex items-end justify-between rounded-2xl bg-unbox-cream-50 py-4 pl-6 pr-4">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="flex shrink-0 items-start -space-x-3">
              {nudgeAvatars.map((item) => {
                const { nickname, handle } = getCreatorName(item.nickname, item.creatorName ?? item.username);
                return (
                  <Avatar
                    key={item.creatorOpenId}
                    size="md"
                    className="ring-[1.5px] ring-bg-primary"
                    src={item.thumbnail ?? item.avatar ?? null}
                    alt={nickname ?? handle ?? undefined}
                    initials={(nickname ?? item.creatorName ?? item.username ?? "?").charAt(0).toUpperCase()}
                  />
                );
              })}
              {nudgeHiddenCount > 0 && (
                <span className="flex size-10 items-center justify-center rounded-full border border-secondary bg-tertiary text-md font-semibold text-quaternary ring-[1.5px] ring-bg-primary">
                  +{nudgeHiddenCount}
                </span>
              )}
            </div>
            <p className="min-w-0 truncate text-sm font-semibold text-primary">
              You've manually blocked {manualBlockCount} creator{manualBlockCount === 1 ? "" : "s"} this week.
            </p>
            <p className="min-w-0 truncate text-sm text-secondary">
              Auto-tag creators who match these conditions - save time every week.
            </p>
          </div>
          <div className="flex items-center gap-3 self-stretch">
            <Button color="primary" size="sm" onClick={() => setQuickCreateRuleOpen(true)}>
              Create a Rule
            </Button>
            <button
              type="button"
              aria-label="Dismiss"
              className="rounded-md p-2 text-fg-quaternary transition-colors hover:text-fg-quaternary_hover"
              onClick={dismissNudge}
            >
              <XClose className="size-5" />
            </button>
          </div>
        </div>
      )}

      {!isEmpty && selected.length > 0 && (
        <div className="flex flex-row justify-between items-center rounded-lg bg-brand-primary -my-2 p-2 flex-shrink-0">
          <div className="flex flex-row items-center gap-2">
            <CloseButton onClick={handleClearSelection} />
            <span className="text-sm font-medium">{selectedCount.toLocaleString()} selected</span>
            <SelectAllPagesButton state={selectAll} />
          </div>
          <Button iconLeading={ReverseLeft} isDisabled={selectAll.isFetching} onClick={() => setBulkUnblockOpen(true)}>
            Unblock
          </Button>
        </div>
      )}

      {!isEmpty && selected.length === 0 && (
        <div className="flex flex-row items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-1.5 text-sm font-medium">
            <span>{total.toLocaleString()} Creators</span>
            <Tooltip title="Blocked creators" description="Creators excluded from recommendations, search and imports.">
              <TooltipTrigger>
                <AlertCircle className="size-3.5 text-quaternary cursor-help" />
              </TooltipTrigger>
            </Tooltip>
          </div>
          <div className="flex items-center flex-row gap-2">
            <Input
              size="sm"
              className="w-72"
              placeholder="Search Creator..."
              icon={SearchMd}
              value={search}
              onChange={(value) => setSearch(value)}
            />
          </div>
        </div>
      )}

      <BlockedCreatorsTable
        rows={loadedItems}
        onClickRow={(item) => {
          if (!item.creatorId) return;
          setSelectedCreatorId(item.creatorId);
          navigate({ to: ".", search: (prev: Record<string, unknown>) => ({ ...prev, creator_id: item.creatorId }) });
        }}
        selection={{
          raw: true,
          selectedKeys: new Set(selected),
          onSelectionChange: handleSelectionChange as (keys: Selection) => void,
        }}
        pagination={{ page: pagination.page, totalPages, onPageChange: pagination.setPage }}
        hasActiveFilters={!!debouncedSearch}
        onClearSearch={() => setSearch("")}
        onCreateRule={() => setQuickCreateRuleOpen(true)}
        onManageAllCohorts={openCohortLibrary}
        onUnblock={setUnblockTarget}
      />

      {unblockTarget && (
        <UnblockCreatorModal
          creatorOpenId={unblockTarget.creatorOpenId}
          creatorName={unblockTarget.creatorName}
          open={!!unblockTarget}
          onOpenChange={(open) => {
            if (!open) setUnblockTarget(undefined);
          }}
        />
      )}

      <CreatorDetails
        id={selectedCreatorId}
        onClose={() => {
          setSelectedCreatorId(undefined);
          navigate({ to: ".", search: (prev: Record<string, unknown>) => ({ ...prev, creator_id: undefined }) });
        }}
      />

      <BulkUnblockCreatorsModal
        creatorOpenIds={selected}
        open={bulkUnblockOpen}
        onOpenChange={setBulkUnblockOpen}
        onSuccess={handleClearSelection}
      />

      <BlocklistRulesPanel open={rulesPanelOpen} onOpenChange={setRulesPanelOpen} />
      <RuleBuilderDrawer open={quickCreateRuleOpen} onOpenChange={setQuickCreateRuleOpen} />
      <CohortLibraryModal open={cohortLibraryOpen} onOpenChange={setCohortLibraryOpen} />

      <BlocklistImportErrorDrawer
        open={!!wrongFileMessage}
        message={wrongFileMessage ?? ""}
        onOpenChange={(open) => {
          if (!open) setWrongFileMessage(null);
        }}
        onDownloadTemplate={downloadImportTemplate}
      />

      {importPreviewRows && (
        <BlocklistImportReviewDrawer
          rows={importPreviewRows}
          onOpenChange={(open) => {
            if (!open) setImportPreviewRows(null);
          }}
        />
      )}
    </PageLayout>
  );
}
